/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListResourcesRequest,
  ListResourcesSecurity,
} from "../models/operations/listresources.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListResourcesQuery,
  ListResourcesQueryData,
  prefetchListResources,
  queryKeyListResources,
} from "./listResources.core.js";
export {
  buildListResourcesQuery,
  type ListResourcesQueryData,
  prefetchListResources,
  queryKeyListResources,
};

export type ListResourcesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listResources resources
 *
 * @remarks
 * List all resources for a project
 */
export function useListResources(
  request?: ListResourcesRequest | undefined,
  security?: ListResourcesSecurity | undefined,
  options?: QueryHookOptions<ListResourcesQueryData, ListResourcesQueryError>,
): UseQueryResult<ListResourcesQueryData, ListResourcesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListResourcesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listResources resources
 *
 * @remarks
 * List all resources for a project
 */
export function useListResourcesSuspense(
  request?: ListResourcesRequest | undefined,
  security?: ListResourcesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListResourcesQueryData,
    ListResourcesQueryError
  >,
): UseSuspenseQueryResult<ListResourcesQueryData, ListResourcesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListResourcesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListResourcesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      deploymentId?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListResourcesQueryData,
): ListResourcesQueryData | undefined {
  const key = queryKeyListResources(...queryKeyBase);

  return client.setQueryData<ListResourcesQueryData>(key, data);
}

export function invalidateListResources(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      deploymentId?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "resources", "list", ...queryKeyBase],
  });
}

export function invalidateAllListResources(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "resources", "list"],
  });
}
